import React, { useEffect, useRef, useState } from "react";
import Icon from "../UI/Icon";
import { setThemePreference, useTheme } from "../../theme/theme";

const OPTIONS = [
  { value: "light", label: "Light", icon: "sun" },
  { value: "dark", label: "Dark", icon: "moon" },
  { value: "system", label: "System", icon: "monitor" },
];

// variant "menu": icon button + popover (desktop header)
// variant "segmented": inline three-way switch (mobile menu)
function ThemeToggle({ variant = "menu" }) {
  const { preference, theme } = useTheme();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const itemRefs = useRef([]);

  useEffect(() => {
    if (!open) return undefined;
    const current = OPTIONS.findIndex((o) => o.value === preference);
    const node = itemRefs.current[current >= 0 ? current : 0];
    if (node) node.focus();
    const onPointer = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    return () => document.removeEventListener("mousedown", onPointer);
  }, [open, preference]);

  if (variant === "segmented") {
    return (
      <div className="v-segmented" role="radiogroup" aria-label="Colour theme">
        {OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={preference === option.value}
            className={`v-segmented__item ${preference === option.value ? "is-active" : ""}`}
            onClick={() => setThemePreference(option.value)}
          >
            <Icon name={option.icon} size={16} />
            {option.label}
          </button>
        ))}
      </div>
    );
  }

  const onMenuKeyDown = (event) => {
    const items = itemRefs.current.filter(Boolean);
    const index = items.indexOf(document.activeElement);
    if (event.key === "ArrowDown") {
      event.preventDefault();
      items[(index + 1) % items.length].focus();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      items[(index - 1 + items.length) % items.length].focus();
    } else if (event.key === "Escape" || event.key === "Tab") {
      setOpen(false);
      if (event.key === "Escape" && rootRef.current) rootRef.current.querySelector("button").focus();
    }
  };

  return (
    <div className="v-menu" ref={rootRef}>
      <button
        type="button"
        className="v-icon-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Theme: ${preference}`}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name={theme === "dark" ? "moon" : "sun"} />
      </button>
      {open ? (
        <div className="v-menu__popover" role="menu" aria-label="Colour theme" onKeyDown={onMenuKeyDown}>
          {OPTIONS.map((option, i) => (
            <button
              key={option.value}
              ref={(node) => {
                itemRefs.current[i] = node;
              }}
              type="button"
              role="menuitemradio"
              aria-checked={preference === option.value}
              className="v-menu__item"
              onClick={() => {
                setThemePreference(option.value);
                setOpen(false);
              }}
            >
              <Icon name={option.icon} size={18} />
              <span>{option.label}</span>
              {preference === option.value ? <Icon name="check" size={16} className="v-menu__check" /> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default ThemeToggle;
